import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  TrendingUp, 
  Shield, 
  Settings,
  BarChart3,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database';

type UserStats = Database['public']['Views']['admin_user_stats']['Row'];
type ContentStats = Database['public']['Views']['admin_content_stats']['Row'];
type RecentActivity = Database['public']['Views']['admin_recent_activity']['Row'];

const AdminDashboard = () => {
  const { user, isAdmin, loading } = useAuth();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [contentStats, setContentStats] = useState<ContentStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  useEffect(() => {
    if (!loading && isAdmin) {
      fetchDashboardData();
    }
  }, [loading, isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);

      // Fetch user stats
      const { data: userStatsData } = await supabase
        .from('admin_user_stats')
        .select('*')
        .single();

      // Fetch content stats
      const { data: contentStatsData } = await supabase
        .from('admin_content_stats')
        .select('*')
        .single();

      // Fetch recent activity
      const { data: activityData } = await supabase
        .from('admin_recent_activity')
        .select('*')
        .limit(10);

      setUserStats(userStatsData);
      setContentStats(contentStatsData);
      setRecentActivity(activityData || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const StatCard = ({ 
    title, 
    value, 
    change, 
    icon, 
    color = 'green' 
  }: { 
    title: string; 
    value: number | null; 
    change?: number | null; 
    icon: React.ReactNode; 
    color?: string;
  }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-white">
            {value?.toLocaleString() || '0'}
          </p>
          {change !== undefined && change !== null && (
            <p className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? '+' : ''}{change}% from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/20`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Welcome back, {user?.name}. Here's what's happening on FemSportNet.
            </p>
          </div>

          {dashboardLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                  title="Total Users"
                  value={userStats?.total_users}
                  change={userStats?.new_users_30d}
                  icon={<Users className="h-6 w-6 text-green-500" />}
                />
                <StatCard
                  title="Total Posts"
                  value={contentStats?.total_posts}
                  change={contentStats?.posts_30d}
                  icon={<FileText className="h-6 w-6 text-blue-500" />}
                  color="blue"
                />
                <StatCard
                  title="Active Athletes"
                  value={userStats?.athletes}
                  icon={<TrendingUp className="h-6 w-6 text-purple-500" />}
                  color="purple"
                />
                <StatCard
                  title="Verified Users"
                  value={userStats?.verified_users}
                  icon={<Shield className="h-6 w-6 text-yellow-500" />}
                  color="yellow"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* User Breakdown */}
                <div className="lg:col-span-2">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      User Breakdown
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Athletes</span>
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ 
                                width: `${userStats?.total_users ? (userStats.athletes! / userStats.total_users) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {userStats?.athletes || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Coaches</span>
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ 
                                width: `${userStats?.total_users ? (userStats.coaches! / userStats.total_users) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {userStats?.coaches || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Admins</span>
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{ 
                                width: `${userStats?.total_users ? (userStats.admins! / userStats.total_users) * 100 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {userStats?.admins || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Quick Actions
                  </h2>
                  <div className="space-y-3">
                    <button className="w-full flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/30 transition-colors">
                      <CheckCircle size={18} className="mr-2" />
                      Verify Users
                    </button>
                    <button className="w-full flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors">
                      <BarChart3 size={18} className="mr-2" />
                      View Analytics
                    </button>
                    <button className="w-full flex items-center px-4 py-2 bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/30 transition-colors">
                      <AlertTriangle size={18} className="mr-2" />
                      Review Reports
                    </button>
                    <button className="w-full flex items-center px-4 py-2 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors">
                      <XCircle size={18} className="mr-2" />
                      Moderate Content
                    </button>
                    <button className="w-full flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                      <Settings size={18} className="mr-2" />
                      Platform Settings
                    </button>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="mt-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Recent Activity
                    </h2>
                    <Activity className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="space-y-4">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {activity.user_name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {activity.description}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            activity.activity_type === 'user' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                            activity.activity_type === 'post' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' :
                            'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                          }`}>
                            {activity.activity_type}
                          </span>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;